<template>
  <div class="video-picker">
    <div class="picker-container">
      <div class="search-panel">
        <div class="search-form">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search by term..."
            @keyup.enter="doSearch"
            autofocus
          />
          <b-button variant="primary" @click="doSearch" :disabled="isSearching">
            Search
          </b-button>
        </div>

        <div class="results-count" v-if="videos.length > 0">
          {{ videos.length }} video{{ videos.length === 1 ? '' : 's' }} found
        </div>
        <div class="results" v-if="videos.length > 0">
          <div
            v-for="video in videos"
            :key="video.id"
            class="video-item"
            :class="{ selected: selectedVideo && selectedVideo.id === video.id }"
            @click="previewVideo(video)"
          >
            <div class="video-info">
              <div class="video-title">{{ video.title }}</div>
              <div class="video-tags" v-if="(video.tags && video.tags.length) || (video.categories && video.categories.length)">
                <span
                  v-for="cat in (video.categories || [])"
                  :key="'cat-' + cat"
                  class="tag-link"
                  @click.stop="searchFor(cat)"
                >{{ cat }}</span>
                <span
                  v-for="tag in (video.tags || [])"
                  :key="'tag-' + tag"
                  class="tag-link"
                  @click.stop="searchFor(tag)"
                >{{ tag }}</span>
              </div>
              <div class="video-account" v-else-if="video.account">{{ video.account }}</div>
            </div>
          </div>
        </div>

        <div v-else-if="searchError && !isSearching" class="no-results">
          {{ searchError }} Please try again, or contact us at hello@truewayasl.com.
        </div>

        <div v-else-if="searchPerformed && !isSearching" class="no-results">
          No videos found. Try a different search term.
        </div>

        <div v-if="isSearching" class="loading">
          <b-spinner small /> Searching...
        </div>
      </div>

      <div class="preview-panel">
        <div class="preview-container">
          <div v-if="!selectedVideo" class="preview-placeholder">
            Select a video to preview
          </div>
          <iframe
            v-else
            :src="previewUrl"
            allowfullscreen
            allow="fullscreen"
          ></iframe>
        </div>
        <div class="preview-title" v-if="selectedVideo">
          {{ selectedVideo.title }}
          <div class="preview-tags" v-if="(selectedVideo.tags && selectedVideo.tags.length) || (selectedVideo.categories && selectedVideo.categories.length)">
            <span
              v-for="cat in (selectedVideo.categories || [])"
              :key="'cat-' + cat"
              class="tag-link"
              @click="searchFor(cat)"
            >{{ cat }}</span>
            <span
              v-for="tag in (selectedVideo.tags || [])"
              :key="'tag-' + tag"
              class="tag-link"
              @click="searchFor(tag)"
            >{{ tag }}</span>
          </div>
        </div>

        <b-button
          variant="success"
          block
          class="insert-btn"
          :disabled="!selectedVideo || isInserting"
          @click="insertVideo"
        >
          {{ isInserting ? 'Inserting...' : 'Insert Video' }}
        </b-button>
      </div>
    </div>

    <!-- Hidden form for content item submission -->
    <form ref="contentForm" method="POST" :action="returnUrl" style="display: none;">
      <input type="hidden" name="lti_message_type" value="ContentItemSelection" />
      <input type="hidden" name="lti_version" value="LTI-1p0" />
      <input type="hidden" name="content_items" :value="contentItemsJson" />
      <input type="hidden" name="data" :value="returnData" />
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import {getErrorMessage} from "@/utils/error-messages"
import {initBeacon, reportErrorToBeacon} from "@/utils/beacon"

const API_URL = process.env.VUE_APP_API_URL

export default {
  name: 'VideoPicker',
  data() {
    return {
      searchQuery: '',
      videos: [],
      selectedVideo: null,
      isSearching: false,
      searchPerformed: false,
      searchError: null,
      contentItemsJson: '',
      isInserting: false
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  computed: {
    returnUrl() {
      return this.$route.query.return_url || ''
    },
    returnData() {
      return this.$route.query.data || ''
    },
    videoLaunchUrl() {
      return `${API_URL}/api/lti/video_launch`
    },
    previewUrl() {
      if (!this.selectedVideo) return ''
      return this.selectedVideo.preview_url || ''
    }
  },
  methods: {
    async doSearch() {
      if (!this.searchQuery.trim()) return

      this.isSearching = true
      this.searchPerformed = true
      this.searchError = null

      try {
        const response = await axios.get(`${API_URL}/api/lti/video_search`, {
          params: { q: this.searchQuery }
        })
        this.videos = response.data.videos || []
      } catch (error) {
        // Surface the failure (silently-empty results read as "no videos found")
        // and arm Beacon so "contact us" carries the underlying error.
        this.searchError = getErrorMessage(error)
        initBeacon()
        reportErrorToBeacon(error, 'Video Search Error', { 'Flow': 'Video Picker' })
        this.videos = []
      }

      this.isSearching = false
    },
    previewVideo(video) {
      this.selectedVideo = video
    },
    searchFor(term) {
      this.searchQuery = term
      this.doSearch()
    },
    handleKeydown(e) {
      if (!this.videos.length) return

      const currentIndex = this.selectedVideo
        ? this.videos.findIndex(v => v.id === this.selectedVideo.id)
        : -1

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const nextIndex = currentIndex < this.videos.length - 1 ? currentIndex + 1 : 0
        this.selectedVideo = this.videos[nextIndex]
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : this.videos.length - 1
        this.selectedVideo = this.videos[prevIndex]
      }
    },
    insertVideo() {
      if (!this.selectedVideo) return

      this.isInserting = true

      // Return LTI link with proper mediaType so Canvas does LTI launch (POST)
      const contentItems = {
        "@context": "http://purl.imsglobal.org/ctx/lti/v1/ContentItem",
        "@graph": [{
          "@type": "LtiLinkItem",
          "mediaType": "application/vnd.ims.lti.v1.ltilink",
          "url": `${this.videoLaunchUrl}?video=${this.selectedVideo.sproutvideo_id}`,
          "title": this.selectedVideo.title,
          "placementAdvice": {
            "presentationDocumentTarget": "iframe",
            "displayWidth": 630,
            "displayHeight": 354
          }
        }]
      }

      this.contentItemsJson = JSON.stringify(contentItems)

      // Submit form on next tick after contentItemsJson is updated
      this.$nextTick(() => {
        this.$refs.contentForm.submit()
      })
    }
  }
}
</script>

<style scoped>
.video-picker {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding: 0 16px 16px 16px;
  height: 100vh;
  box-sizing: border-box;
}

.picker-container {
  display: flex;
  gap: 16px;
  height: 100%;
}

.search-panel {
  width: 45%;
  display: flex;
  flex-direction: column;
}

.search-form {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.search-form input {
  flex: 1;
}

.results {
  flex: 1;
  overflow-y: auto;
}

.video-item {
  padding: 8px 12px;
  margin: 4px 0;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  cursor: pointer;
}

.video-item:hover {
  background: #e9ecef;
}

.video-item.selected {
  background: #cce5ff;
  border-color: #0066cc;
}

.video-title {
  font-weight: 500;
  color: #333;
  font-size: 16px;
}

.video-tags {
  font-size: 14px;
  color: #888;
  margin-top: 2px;
}

.video-account {
  font-size: 13px;
  color: #999;
  margin-top: 2px;
}

.tag-link {
  color: #0066cc;
  cursor: pointer;
}

.tag-link:hover {
  text-decoration: underline;
}

.tag-link:not(:last-child)::after {
  content: ', ';
  color: #888;
}

.preview-panel {
  width: 55%;
  display: flex;
  flex-direction: column;
}

.preview-container {
  background: #000;
  border-radius: 4px;
  overflow: hidden;
}

.preview-container iframe {
  width: 100%;
  aspect-ratio: 16/9;
  border: none;
  display: block;
}

.preview-placeholder {
  color: #999;
  padding: 40px;
  text-align: center;
  background: #f0f0f0;
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-title {
  margin: 8px 0;
  font-size: 16px;
  color: #333;
}

.preview-tags {
  font-size: 14px;
  color: #888;
  margin-top: 2px;
}

.insert-btn {
  margin-top: 8px;
}

.results-count {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.no-results,
.loading {
  color: #666;
  padding: 20px;
  text-align: center;
}
</style>
